"use client"

import type React from "react"

import { cn } from "@/lib/utils"

import { useEffect } from "react"

import { useState } from "react"

interface FloatingBadgeProps {
  children: React.ReactNode
  className?: string
  amplitude?: number
  rotation?: number
  delay?: number
}

export function FloatingBadge({ children, className, amplitude = 5, rotation = 0, delay = 0 }: FloatingBadgeProps) {
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsAnimating(true)

      const animate = () => {
        const newX = (Math.random() - 0.5) * 2 * amplitude
        const newY = (Math.random() - 0.5) * 2 * amplitude

        setOffset({ x: newX, y: newY })
      }

      animate()
      const interval = setInterval(animate, 3000)

      return () => clearInterval(interval)
    }, delay)

    return () => clearTimeout(timeout)
  }, [amplitude, delay])

  return (
    <div
      className={cn("relative transition-transform", className)}
      style={{
        transform: isAnimating ? `translate(${offset.x}px, ${offset.y}px) rotate(${rotation}deg)` : "none",
        transitionDuration: "3s",
        transitionTimingFunction: "ease-in-out",
      }}
    >
      {children}
    </div>
  )
}

interface PulseIconProps {
  children: React.ReactNode
  className?: string
  pulseColor?: string
  delay?: number
  duration?: number
}

export function PulseIcon({
  children,
  className,
  pulseColor = "rgba(255, 78, 0, 0.5)",
  delay = 0,
  duration = 2,
}: PulseIconProps) {
  const [isPulsing, setIsPulsing] = useState(false)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsPulsing(true)
    }, delay)

    return () => clearTimeout(timeout)
  }, [delay])

  return (
    <span
      className={cn("relative inline-block", isPulsing && "after:animate-scale-pulse", className)}
      style={
        {
          "--pulse-color": pulseColor,
          "--pulse-duration": `${duration}s`,
        } as React.CSSProperties
      }
    >
      {children}
    </span>
  )
}

interface TypewriterTextProps {
  text: string
  className?: string
  delay?: number
  speed?: number
}

export function TypewriterText({ text, className, delay = 0, speed = 50 }: TypewriterTextProps) {
  const [displayedText, setDisplayedText] = useState("")

  useEffect(() => {
    let timeout: NodeJS.Timeout

    timeout = setTimeout(() => {
      let currentIndex = 0

      const interval = setInterval(() => {
        if (currentIndex <= text.length) {
          setDisplayedText(text.substring(0, currentIndex))
          currentIndex++
        } else {
          clearInterval(interval)
        }
      }, speed)

      return () => clearInterval(interval)
    }, delay)

    return () => clearTimeout(timeout)
  }, [text, delay, speed])

  return <span className={className}>{displayedText}</span>
}

interface CountUpProps {
  end: number
  className?: string
  duration?: number
  delay?: number
  prefix?: string
  suffix?: string
}

export function CountUp({ end, className, duration = 2, delay = 0, prefix = "", suffix = "" }: CountUpProps) {
  const [count, setCount] = useState(0)

  useEffect(() => {
    let timeout: NodeJS.Timeout

    timeout = setTimeout(() => {
      const increment = end / (duration * 60) // Assumes 60 frames per second
      let currentCount = 0

      const interval = setInterval(() => {
        currentCount += increment
        if (currentCount >= end) {
          clearInterval(interval)
          setCount(end)
        } else {
          setCount(Math.floor(currentCount))
        }
      }, 1000 / 60)

      return () => clearInterval(interval)
    }, delay)

    return () => clearTimeout(timeout)
  }, [end, duration, delay])

  return <span className={className}>{prefix + count + suffix}</span>
}

interface MagneticButtonProps {
  children: React.ReactNode
  className?: string
  onClick?: () => void
}

export function MagneticButton({ children, className, onClick }: MagneticButtonProps) {
  return (
    <button className={cn("relative overflow-hidden magnetic-hover", className)} onClick={onClick}>
      {children}
    </button>
  )
}

interface GentleRevealTextProps {
  texts: string[]
  className?: string
  delay?: number
  staggerDelay?: number
}

export function GentleRevealText({ texts, className, delay = 500, staggerDelay = 800 }: GentleRevealTextProps) {
  const [visibleIndices, setVisibleIndices] = useState<number[]>([])

  useEffect(() => {
    if (texts.length === 0) return

    // Révéler progressivement chaque texte
    const timeouts: NodeJS.Timeout[] = []

    texts.forEach((_, index) => {
      const timeout = setTimeout(
        () => {
          setVisibleIndices((prev) => [...prev, index])
        },
        delay + index * staggerDelay,
      )

      timeouts.push(timeout)
    })

    return () => timeouts.forEach((t) => clearTimeout(t))
  }, [texts, delay, staggerDelay])

  return (
    <div className={cn("space-y-2", className)}>
      {texts.map((text, index) => (
        <div
          key={index}
          className={cn(
            "transition-all duration-500 ease-out min-h-[1.5em]",
            visibleIndices.includes(index) ? "opacity-100 transform-none" : "opacity-0 translate-y-4",
          )}
          aria-hidden={!visibleIndices.includes(index)}
        >
          {text}
        </div>
      ))}
    </div>
  )
}

// Nouvelle animation de texte avec un effet plus dynamique
interface ShimmerTextProps {
  text: string
  className?: string
  delay?: number
}

export function ShimmerText({ text, className, delay = 0 }: ShimmerTextProps) {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsVisible(true)
    }, delay)

    return () => clearTimeout(timeout)
  }, [delay])

  return (
    <span
      className={cn("inline-block relative", isVisible ? "text-gradient animate-text-shimmer" : "opacity-0", className)}
      style={{
        transitionProperty: "opacity",
        transitionDuration: "0.5s",
        transitionTimingFunction: "ease-out",
        transitionDelay: `${delay}ms`,
      }}
    >
      {text}
    </span>
  )
}

// Remplacer complètement le composant TextSlider par cette nouvelle implémentation plus douce

// Composant pour afficher une liste de textes avec un effet de défilement très doux
interface TextSliderProps {
  texts: string[]
  className?: string
  delay?: number
  duration?: number
}

export function TextSlider({ texts, className, delay = 500, duration = 6000 }: TextSliderProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [previousIndex, setPreviousIndex] = useState<number | null>(null)
  const [isTransitioning, setIsTransitioning] = useState(false)

  useEffect(() => {
    if (texts.length <= 1) return

    // Démarrer l'animation après le délai initial
    const initialTimeout = setTimeout(() => {
      // Changer de texte à intervalles réguliers
      const interval = setInterval(() => {
        // Sauvegarder l'index actuel comme précédent
        setPreviousIndex(currentIndex)

        // Commencer la transition
        setIsTransitioning(true)

        // Calculer le prochain index
        const nextIndex = (currentIndex + 1) % texts.length

        // Après un délai pour la transition de sortie, changer l'index
        setTimeout(() => {
          setCurrentIndex(nextIndex)

          // Réinitialiser l'état de transition après la fin complète
          setTimeout(() => {
            setIsTransitioning(false)
            setPreviousIndex(null)
          }, 1000) // Délai après changement pour terminer la transition
        }, 1500) // Délai avant de changer l'index (transition de sortie)
      }, duration)

      return () => clearInterval(interval)
    }, delay)

    return () => clearTimeout(initialTimeout)
  }, [texts, delay, duration, currentIndex])

  return (
    <div className={cn("relative h-[120px] md:h-[100px] flex flex-col justify-center overflow-hidden", className)}>
      {/* Texte précédent (en train de sortir) */}
      {previousIndex !== null && (
        <div
          className="absolute w-full transition-all duration-[2000ms] ease-in-out opacity-0 -translate-y-3"
          style={{
            transitionTimingFunction: "cubic-bezier(0.4, 0.0, 0.2, 1)",
          }}
          aria-hidden="true"
        >
          <div className="text-podspace-white">{texts[previousIndex]}</div>
        </div>
      )}

      {/* Texte actuel (en train d'entrer ou stable) */}
      <div
        className={cn(
          "absolute w-full transition-all",
          isTransitioning
            ? "opacity-0 translate-y-3 duration-[0ms]" // Caché, prêt à entrer
            : "opacity-100 translate-y-0 duration-[2000ms]", // Visible avec transition d'entrée
        )}
        style={{
          transitionTimingFunction: "cubic-bezier(0.4, 0.0, 0.2, 1)",
          transitionDelay: isTransitioning ? "0ms" : "1000ms", // Délai pour l'entrée
        }}
      >
        <div className="text-podspace-white">{texts[currentIndex]}</div>
      </div>
    </div>
  )
}
